import { useState, useRef } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera, Lock } from "lucide-react";

export default function UserAccount() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState("");

  // Get user profile
  const { data: profile, isLoading: profileLoading, refetch: refetchProfile } = trpc.userAccount.getProfile.useQuery();
  
  // Get user addresses
  const { data: addresses = [] } = trpc.userAccount.getAddresses.useQuery();
  
  // Get user orders
  const { data: ordersData } = trpc.userAccount.getOrders.useQuery({ page: 1, pageSize: 10 });
  
  // Get user favorites
  const { data: favorites = [] } = trpc.userAccount.getFavorites.useQuery();

  // Update profile
  const updateProfileMutation = trpc.userAccount.updateProfile.useMutation({
    onSuccess: () => {
      toast.success("Profile updated successfully");
      refetchProfile();
    },
    onError: (error) => {
      toast.error(error.message || "Update failed");
    },
  });

  // Upload avatar
  const uploadAvatarMutation = trpc.userAccount.uploadAvatar.useMutation({
    onSuccess: (data) => {
      toast.success("Avatar uploaded successfully");
      setAvatarPreview(null);
      refetchProfile();
    },
    onError: (error) => {
      toast.error(error.message || "Avatar upload failed");
    },
  });

  // Change password
  const changePasswordMutation = trpc.userAccount.changePassword.useMutation({
    onSuccess: () => {
      toast.success("Password changed successfully");
      setPasswordError("");
      // Reset form
      const form = document.getElementById("passwordForm") as HTMLFormElement;
      if (form) form.reset();
    },
    onError: (error) => {
      setPasswordError(error.message || "Password change failed");
      toast.error(error.message || "Password change failed");
    },
  });

  // Create address
  const createAddressMutation = trpc.userAccount.createAddress.useMutation({
    onSuccess: () => {
      toast.success("Address added successfully");
    },
  });

  // Logout
  const utils = trpc.useUtils();

  const logoutMutation = trpc.userAuth.userLogout.useMutation({
    onSuccess: () => {
      // Clear all trpc cache
      utils.auth.me.setData(undefined, null);
      utils.auth.me.invalidate();
      
      toast.success("Logged out successfully");
      // Reload page
      setTimeout(() => {
        window.location.href = "/";
      }, 500);
    },
    onError: (error) => {
      toast.error(error.message || "Logout failed");
    },
  });

  const handleUpdateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const genderValue = formData.get("gender") as string;
    const data = {
      name: formData.get("name") as string,
      phone: formData.get("phone") as string,
      gender: (genderValue || undefined) as "male" | "female" | "other" | undefined,
      bio: formData.get("bio") as string,
      preferredLanguage: formData.get("language") as string,
    };

    try {
      await updateProfileMutation.mutateAsync(data);
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }

      // Preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarPreview) return;

    try {
      await uploadAvatarMutation.mutateAsync({ avatarBase64: avatarPreview, mimeType: "image/jpeg" });
    } catch (error) {
      toast.error("Avatar upload failed");
    }
  };

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const oldPassword = formData.get("oldPassword") as string;
    const newPassword = formData.get("newPassword") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        oldPassword,
        newPassword,
        confirmPassword,
      });
    } catch (error) {
      console.error("Error changing password:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Please log in first</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">My Account</h1>
          <p className="text-gray-600">Manage your personal information, addresses, orders and favorites</p>
        </div>
        <Button
          variant="destructive"
          onClick={handleLogout}
          className="mt-2"
        >
          Logout
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="addresses">Addresses</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="favorites">Favorites</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-4">
          {/* Avatar Upload Card */}
          <Card>
            <CardHeader>
              <CardTitle>Avatar</CardTitle>
              <CardDescription>Upload or change your avatar</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-6">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={avatarPreview || profile?.avatar || undefined} />
                  <AvatarFallback>{user.name?.charAt(0) || "U"}</AvatarFallback>
                </Avatar>
                
                <div className="space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    Choose Image
                  </Button>
                  {avatarPreview && (
                    <Button
                      type="button"
                      onClick={handleUploadAvatar}
                      disabled={uploadAvatarMutation.isPending}
                      className="w-full"
                    >
                      {uploadAvatarMutation.isPending ? "Uploading..." : "Upload Avatar"}
                    </Button>
                  )}
                  <p className="text-xs text-gray-500">
                    Supports JPG, PNG formats, maximum 5MB
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information Card */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
              <CardDescription>Update your profile information</CardDescription>
            </CardHeader>
            <CardContent>
              {profileLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner />
                </div>
              ) : (
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        defaultValue={user.email || ""}
                        placeholder="Enter your email"
                        className="mt-1"
                        disabled
                      />
                    </div>
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        type="text"
                        name="name"
                        defaultValue={user.name || ""}
                        placeholder="Enter your name"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        defaultValue={profile?.phone || ""}
                        placeholder="Enter your phone number"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender</Label>
                      <select
                        id="gender"
                        name="gender"
                        defaultValue={profile?.gender || ""}
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="bio">Bio</Label>
                    <textarea
                      id="bio"
                      name="bio"
                      defaultValue={profile?.bio || ""}
                      placeholder="Tell us about yourself"
                      rows={4}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>

                  <div>
                    <Label htmlFor="language">Preferred Language</Label>
                    <select
                      id="language"
                      name="language"
                      defaultValue={profile?.preferredLanguage || "en"}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="en">English</option>
                      <option value="zh">Chinese</option>
                      <option value="es">Español</option>
                    </select>
                  </div>

                  <Button type="submit" disabled={updateProfileMutation.isPending}>
                    {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
              <CardDescription>Update your account password to keep your account secure</CardDescription>
            </CardHeader>
            <CardContent>
              <form id="passwordForm" onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && (
                  <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {passwordError}
                  </div>
                )}

                <div>
                  <Label htmlFor="oldPassword">Old Password</Label>
                  <Input
                    id="oldPassword"
                    name="oldPassword"
                    type="password"
                    placeholder="Enter your old password"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    placeholder="Enter new password (at least 6 characters)"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Re-enter new password"
                    className="mt-1"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={changePasswordMutation.isPending}
                  className="w-full"
                >
                  {changePasswordMutation.isPending ? "Changing..." : "Change Password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Addresses Tab */}
        <TabsContent value="addresses" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>My Addresses</CardTitle>
              <CardDescription>Manage your shipping addresses</CardDescription>
            </CardHeader>
            <CardContent>
              {addresses.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-4">You haven't added any shipping addresses yet</p>
                  <Button onClick={() => setActiveTab("addresses")}>Add Address</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {addresses.map((address) => (
                    <Card key={address.id} className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{address.recipientName}</h3>
                        {address.isDefault === 1 && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Default Address
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {address.street}, {address.city}, {address.state} {address.postalCode}
                      </p>
                      <p className="text-sm text-gray-600">{address.country}</p>
                      <p className="text-sm text-gray-600">Phone: {address.recipientPhone}</p>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Order History</CardTitle>
              <CardDescription>View your order records</CardDescription>
            </CardHeader>
            <CardContent>
              {!ordersData || ordersData.orders.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">You don't have any orders yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {ordersData.orders.map((order) => (
                    <Card key={order.id} className="p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold">Order #: {order.orderNumber}</h3>
                          <p className="text-sm text-gray-600">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {order.status}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm">Total: ${order.totalAmount}</p>
                        <Button variant="outline" size="sm">
                          View Details
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Favorites Tab */}
        <TabsContent value="favorites" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>My Favorites</CardTitle>
              <CardDescription>Products you've saved</CardDescription>
            </CardHeader>
            <CardContent>
              {favorites.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">You haven't saved any products yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {favorites.map((favorite) => (
                    <Card key={favorite.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold">Product ID: {favorite.productId}</h3>
                          <p className="text-sm text-gray-600">
                            Saved on: {new Date(favorite.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Button variant="outline" size="sm">
                          View Product
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
